/**
 * Familles de modèles (« Cayenne » pour Cayenne II et Cayenne III). La facette modèle filtre toujours
 * sur la désignation (`model` de l'index, adresses `refinementList[model]`, alertes, pages dynamiques) ;
 * la marketplace en regroupe seulement les valeurs par famille pour l'affichage, et choisir une famille
 * choisit toutes ses désignations. La table vient du serveur (`VehicleModel::familiesByDesignation`) :
 * un modèle sans famille est sa propre famille, et sans table rien ne change.
 */

export type ModelFamilies = Record<string, string>;

export interface ModelFacetItemLike {
    value: string;
    label: string;
    count: number;
    isRefined: boolean;
}

/** Table servie avec la page (`[]` en JSON quand elle est vide). */
export function modelFamilies(): ModelFamilies {
    if (typeof window === "undefined") return {};
    const map = window.__MARKETPLACE_CONFIG__?.modelFamilies;

    return map && typeof map === "object" && !Array.isArray(map) ? map : {};
}

/** Famille d'une désignation ; la désignation elle-même sans famille. */
export function familyOf(designation: string, map: ModelFamilies = modelFamilies()): string {
    return Object.prototype.hasOwnProperty.call(map, designation) ? map[designation] : designation;
}

/** Familles d'une liste de désignations, sans doublon, dans l'ordre. */
export function familiesOf(designations: string[], map: ModelFamilies = modelFamilies()): string[] {
    return [...new Set(designations.map((designation) => familyOf(designation, map)))];
}

/**
 * Désignations d'une famille : celles de la table, plus les valeurs connues (facette, filtres actifs)
 * qui y tombent. Une famille sans entrée dans la table n'a qu'elle-même.
 */
export function familyMembers(
    family: string,
    known: string[] = [],
    map: ModelFamilies = modelFamilies(),
): string[] {
    const members = new Set<string>();
    Object.entries(map).forEach(([designation, value]) => {
        if (value === family) members.add(designation);
    });
    known.forEach((value) => {
        if (familyOf(value, map) === family) members.add(value);
    });
    if (members.size === 0) members.add(family);

    return [...members];
}

/**
 * Une ligne par famille, cochée si une de ses désignations l'est. Son nombre : les véhicules des
 * désignations choisies quand elle est cochée (ce que montre la liste, ex. une ancienne adresse
 * « 911 Targa » seule), sinon de toutes ses désignations. Ordre de la première désignation
 * rencontrée ; `resort` remet les familles cochées puis les plus fournies en tête (tri de la
 * facette). Sans table, la liste est rendue telle quelle.
 */
export function groupModelFacetItems<T extends ModelFacetItemLike>(
    items: T[],
    { resort = false, map = modelFamilies() }: { resort?: boolean; map?: ModelFamilies } = {},
): T[] {
    if (Object.keys(map).length === 0) return items;

    const groups = new Map<string, { item: T; total: number; refined: number }>();
    items.forEach((item) => {
        const family = familyOf(item.value, map);
        const group = groups.get(family);
        if (group) {
            group.total += item.count;
            group.refined += item.isRefined ? item.count : 0;
            group.item.isRefined = group.item.isRefined || item.isRefined;
            return;
        }
        groups.set(family, {
            item: { ...item, value: family, label: family === item.value ? item.label : family },
            total: item.count,
            refined: item.isRefined ? item.count : 0,
        });
    });

    const grouped = [...groups.values()].map(({ item, total, refined }) => {
        item.count = item.isRefined ? refined : total;
        return item;
    });
    if (!resort) return grouped;

    return grouped
        .map((item, index) => ({ item, index }))
        .sort(
            (a, b) =>
                Number(b.item.isRefined) - Number(a.item.isRefined) ||
                b.item.count - a.item.count ||
                a.index - b.index,
        )
        .map(({ item }) => item);
}

/**
 * Désignations à basculer pour (dé)choisir une famille : celles déjà choisies pour la retirer, sinon
 * toutes ses désignations connues pour l'ajouter.
 */
export function familyToggleValues(
    family: string,
    refined: string[],
    known: string[] = [],
    map: ModelFamilies = modelFamilies(),
): string[] {
    const members = familyMembers(family, [...known, ...refined], map);
    const active = members.filter((member) => refined.includes(member));

    return active.length > 0 ? active : members;
}

/** Nombres d'une facette `model` additionnés par famille (pastilles de la barre), ordre conservé. */
export function groupModelFacetCounts(
    counts: { value: string; count: number }[] | null,
    map: ModelFamilies = modelFamilies(),
): { value: string; count: number }[] | null {
    if (!counts || Object.keys(map).length === 0) return counts;

    const totals = new Map<string, number>();
    counts.forEach(({ value, count }) => {
        const family = familyOf(value, map);
        totals.set(family, (totals.get(family) ?? 0) + count);
    });

    return [...totals].map(([value, count]) => ({ value, count }));
}

/**
 * Valeurs de la facette `model` demandées quand la table des familles existe : toutes les générations
 * sont additionnées avant que la liste soit coupée pour l'affichage (« Voir plus » porte sur les
 * familles). Le serveur envoie une borne sûre (`VehicleModel::modelFacetFetchLimit()` : nombre de
 * désignations distinctes, au moins 1 000) ; 1 000 sans elle.
 */
export const MODEL_FAMILY_FACET_FETCH_LIMIT = 1000;

export function modelFamilyFacetFetchLimit(): number {
    const bound = typeof window === "undefined" ? undefined : window.__MARKETPLACE_CONFIG__?.modelFacetFetchLimit;

    return typeof bound === "number" && Number.isFinite(bound) && bound > MODEL_FAMILY_FACET_FETCH_LIMIT
        ? Math.floor(bound)
        : MODEL_FAMILY_FACET_FETCH_LIMIT;
}

/**
 * La famille (ligne regroupée) répond-elle à la recherche du filtre ? Son nom ou une de ses
 * désignations (« électrique » → Cayenne électrique, « Cayenne III » → Cayenne).
 */
export function familyMatchesQuery(
    family: string,
    query: string,
    known: string[] = [],
    map: ModelFamilies = modelFamilies(),
): boolean {
    const q = query.trim().toLowerCase();
    if (q === "") return true;
    if (family.toLowerCase().includes(q)) return true;

    return familyMembers(family, known, map).some((designation) => designation.toLowerCase().includes(q));
}

interface UiStateInstance {
    setUiState?: (
        updater: (uiState: Record<string, Record<string, unknown>>) => Record<string, Record<string, unknown>>,
    ) => void;
    mainIndex?: { getIndexId?: () => string };
}

/**
 * Bascule plusieurs désignations d'un coup (une famille) : un seul changement d'état, donc une seule
 * recherche et, sur une landing, une seule navigation vers la marketplace avec toutes les désignations.
 * Les retirer si l'une d'elles est choisie, sinon les ajouter toutes. Une seule valeur : `refine`.
 */
export function toggleFacetValuesOnce(
    instance: UiStateInstance | null | undefined,
    attribute: string,
    values: string[],
    refine: (value: string) => void,
): void {
    const indexId = instance?.mainIndex?.getIndexId?.();
    if (values.length <= 1 || !instance?.setUiState || !indexId) {
        values.forEach((value) => refine(value));
        return;
    }

    instance.setUiState((uiState) => {
        const indexState = { ...(uiState[indexId] ?? {}) } as Record<string, unknown> & {
            refinementList?: Record<string, string[]>;
        };
        const list = indexState.refinementList?.[attribute] ?? [];
        const removing = values.some((value) => list.includes(value));
        const next = removing
            ? list.filter((value) => !values.includes(value))
            : [...list, ...values.filter((value) => !list.includes(value))];
        const refinementList = { ...(indexState.refinementList ?? {}) };
        if (next.length > 0) refinementList[attribute] = next;
        else delete refinementList[attribute];
        indexState.refinementList = refinementList;
        delete indexState.page;

        return { ...uiState, [indexId]: indexState };
    });
}
